/**
 * Helpers puros (sem dependencias de Vue) do combo de codigo com digitacao
 * livre (`components/form/ComboCodigo`). Nenhuma funcao daqui produz efeito
 * colateral nem muta os argumentos recebidos.
 *
 * Sao genericos de proposito: qualquer tela que tenha campo de codigo do ERP
 * (Descontos, Precos Promocionais, Restricao de Venda) usa daqui.
 */

import { normalizeString, removeDuplicados, stringContainsObject } from '@/helpers/utils';

/**
 * Indica se o valor deve ser tratado como "sem selecao".
 * @param {*} valor
 * @returns {boolean}
 */
export function isVazio(valor) {
  return valor === null || valor === undefined || valor === '';
}

/**
 * Compara dois valores de codigo ignorando o tipo (a API pode devolver numero
 * onde o form guarda string, e vice-versa).
 * @param {*} a
 * @param {*} b
 * @returns {boolean}
 */
export function mesmoValor(a, b) {
  if (isVazio(a) || isVazio(b)) return false;
  return String(a) === String(b);
}

/**
 * Le a descricao de uma opcao aceitando os dois formatos da API: `descricao`
 * (endpoint de opcoes) e `desc` (dimensoes).
 * @param {{descricao?: *, desc?: *}} opcao
 * @returns {*}
 */
export function descricaoDe(opcao) {
  if (!opcao || typeof opcao !== 'object') return null;
  return isVazio(opcao.descricao) ? opcao.desc : opcao.descricao;
}

/**
 * Normaliza uma lista de opcoes vinda da API para `{ id, descricao }`,
 * descartando entradas invalidas (sem id) e duplicadas.
 * @param {Array<{id: *, descricao?: *, desc?: *}>} opcoes
 * @returns {Array<{id: *, descricao: string}>}
 */
export function normalizeOpcoes(opcoes) {
  if (!Array.isArray(opcoes)) return [];

  const validas = opcoes
    .filter((opcao) => opcao && typeof opcao === 'object' && !isVazio(opcao.id))
    .map((opcao) => {
      const descricao = descricaoDe(opcao);

      return {
        id: opcao.id,
        descricao: isVazio(descricao) ? String(opcao.id) : String(descricao),
      };
    });

  return removeDuplicados(validas, 'id');
}

/**
 * Indica se ha opcoes suficientes para renderizar o dropdown (do contrario o
 * componente mantem o input de texto como fallback).
 * @param {Array} opcoes
 * @returns {boolean}
 */
export function temOpcoes(opcoes) {
  return Array.isArray(opcoes) && opcoes.length > 0;
}

/**
 * Formata o texto de uma opcao como "id - descricao". Quando a descricao for
 * igual ao id (ou inexistente) devolve apenas o codigo.
 * @param {{id: *, descricao?: *, desc?: *}} opcao
 * @returns {string}
 */
export function labelOpcao(opcao) {
  if (!opcao || typeof opcao !== 'object' || isVazio(opcao.id)) return '';

  const id = String(opcao.id);
  const bruta = descricaoDe(opcao);

  if (isVazio(bruta)) return id;

  const descricao = String(bruta);

  return descricao === id ? id : `${id} - ${descricao}`;
}

/**
 * Devolve as opcoes do dropdown garantindo que o valor atualmente gravado no
 * form continue selecionavel: se ele nao existir mais na lista da API, uma
 * opcao "legado" e adicionada no topo. Valor vazio nao gera opcao extra.
 * @param {Array<{id: *, descricao?: *, desc?: *}>} opcoes
 * @param {*} valorAtual
 * @returns {Array<{id: *, descricao: string, legado?: boolean}>}
 */
export function opcoesComValorAtual(opcoes, valorAtual) {
  const lista = normalizeOpcoes(opcoes);

  if (isVazio(valorAtual)) return lista;
  if (lista.some((opcao) => mesmoValor(opcao.id, valorAtual))) return lista;

  return [{ id: valorAtual, descricao: String(valorAtual), legado: true }].concat(lista);
}

/**
 * Alinha o valor do form ao tipo do id devolvido pela API (evita que o
 * dropdown fique em branco por diferenca entre `1` e `"1"`).
 * @param {*} valorAtual
 * @param {Array<{id: *}>} opcoes
 * @returns {*} o id equivalente da lista, ou o proprio valor original
 */
export function alinhaValorComOpcoes(valorAtual, opcoes) {
  if (isVazio(valorAtual)) return valorAtual;

  const encontrada = normalizeOpcoes(opcoes).find((opcao) => mesmoValor(opcao.id, valorAtual));

  return encontrada ? encontrada.id : valorAtual;
}

/**
 * Normaliza texto para comparacao de busca: string, sem acentos e minusculo.
 * Delega em `normalizeString` (helpers/utils) para nao divergir do resto do
 * painel.
 * @param {*} valor
 * @returns {string}
 */
export function normalizaTexto(valor) {
  if (isVazio(valor)) return '';

  return normalizeString(valor).toLowerCase();
}

/**
 * Filtra as opcoes do dropdown pelo termo digitado, comparando codigo e
 * descricao sem diferenciar caixa nem acentos (via `stringContainsObject`, o
 * mesmo comparador usado nas outras buscas locais do painel). Termo vazio
 * devolve a lista inteira (o usuario apenas abriu o combo).
 * @param {Array<{id: *, descricao?: *, desc?: *}>} opcoes
 * @param {*} termo
 * @returns {Array}
 */
export function filtraOpcoes(opcoes, termo) {
  const lista = Array.isArray(opcoes) ? opcoes : [];

  if (!normalizaTexto(termo)) return lista;

  return lista.filter((opcao) => {
    if (!opcao || typeof opcao !== 'object' || isVazio(opcao.id)) return false;

    const descricao = descricaoDe(opcao);

    // objeto plano (sem campos undefined) para o comparador nao poluir a
    // string de comparacao com "undefined"
    return stringContainsObject(termo, ['id', 'descricao'], {
      id: opcao.id,
      descricao: isVazio(descricao) ? '' : descricao,
    });
  });
}

/**
 * Indica se o texto digitado tem "cara de codigo" do ERP e portanto pode ser
 * gravado como valor mesmo sem existir na lista de opcoes (descontos legados e
 * sincronizados do ERP dependem disso).
 *
 * O padrao foi conferido contra os dados reais de clientes em producao: todos
 * os codigos gravados em `desconto` (filial, praca, rede, dimensao, cliente,
 * produto...) usam apenas alfanumericos, ponto, hifen, underscore e barra
 * (ex.: `AREA-52`). Texto com espaco ou acento e busca por descricao, nao
 * codigo.
 * @param {*} texto
 * @returns {boolean}
 */
export function pareceCodigo(texto) {
  if (isVazio(texto)) return false;

  return /^[A-Za-z0-9._/-]+$/.test(String(texto).trim());
}

/**
 * Decide o que fazer com o texto digitado quando o usuario sai do campo sem
 * escolher uma opcao da lista.
 *
 * Como o mesmo input serve para digitar codigo e para buscar por descricao,
 * gravar o texto cru faria "Filial Centro" virar o valor de `filial_id`. A
 * ordem de resolucao e:
 *
 * 1. `limpar`     — campo vazio;
 * 2. `selecionar` — o texto e exatamente o codigo de uma opcao;
 * 3. `codigo`     — campo de codigo livre (id textual no ERP): grava o texto;
 * 4. `selecionar` — o filtro deixou uma unica opcao e o texto casou com a
 *                   *descricao* dela (o usuario buscou e so faltou clicar);
 * 5. `codigo`     — nao casou, mas o campo nao tem lista (ERP sem cadastro) ou
 *                   o texto tem cara de codigo: grava como esta, que e o que
 *                   preserva desconto legado / sincronizado;
 * 6. `descartar`  — era busca por descricao sem resultado: mantem o valor atual.
 *
 * A restricao da regra 4 e o que protege codigo legado: `filtraOpcoes` casa por
 * *substring*, entao digitar `13` havendo `130 - FILIAL NORTE` deixa uma unica
 * opcao visivel e a regra antiga gravava `130` — outra filial, em silencio.
 * Quando o texto e pedaco do *codigo* da opcao, e codigo sendo digitado; quando
 * casou pela descricao, e busca. Nao serve olhar so `pareceCodigo` aqui: uma
 * palavra sem espaco nem acento (`SUL`) tambem "parece codigo" e ainda assim e
 * busca por descricao.
 *
 * @param {*} texto o que esta digitado no input
 * @param {Array<{id: *}>} opcoesVisiveis opcoes apos o filtro da digitacao
 * @param {Array} opcoes lista completa do campo (vazia = campo sem cadastro)
 * @param {boolean} [codigoLivre] campo cujo id e textual no ERP (praca, rede,
 *   fornecedor): qualquer texto digitado vale como codigo
 * @returns {{acao: 'limpar'|'selecionar'|'codigo'|'descartar', valor: *}}
 */
export function resolveValorDigitado(texto, opcoesVisiveis, opcoes, codigoLivre = false) {
  const valor = isVazio(texto) ? '' : String(texto).trim();

  if (valor === '') return { acao: 'limpar', valor: null };

  const visiveis = Array.isArray(opcoesVisiveis) ? opcoesVisiveis : [];
  const exata = visiveis.find((opcao) => opcao && mesmoValor(opcao.id, valor));

  if (exata) return { acao: 'selecionar', valor: exata.id };

  if (codigoLivre) return { acao: 'codigo', valor };

  const unica = visiveis.length === 1 && visiveis[0] ? visiveis[0] : null;
  const pedacoDoCodigo =
    unica && normalizaTexto(unica.id).includes(normalizaTexto(valor));

  if (unica && !pedacoDoCodigo) return { acao: 'selecionar', valor: unica.id };

  if (!temOpcoes(opcoes) || pareceCodigo(valor)) return { acao: 'codigo', valor };

  return { acao: 'descartar', valor: null };
}
